import React, { useState } from 'react';
import { X, Zap } from 'lucide-react';
import TriggerConditionPanel from './TriggerConditionPanel';
import TriggerEventPanel from './TriggerEventPanel';
import { Condition } from '../../../../types/segments/segments.types';
import Select from '../../../ui/Select';

export type TriggerFrequency = 'one_time' | 'every_re_match';

export const TRIGGER_FREQUENCY_OPTIONS = [
  { value: 'one_time', label: 'One time' },
  { value: 'every_re_match', label: 'Every re-match' },
] as const;

interface TriggerPanelProps {
  onClose: () => void;
  onSave: (data: any) => void;
  triggerType: 'attribute-segment' | 'event';
  event?: string;
  conditions?: { [key: string]: Condition[] };
  onConditionsChange: (conditions: { [key: string]: Condition[] }) => void;
  conditionsModified?: boolean;
  campaignId?: string;
  initialFrequency?: TriggerFrequency;
}

const TriggerPanel: React.FC<TriggerPanelProps> = ({
  onClose,
  onSave,
  triggerType,
  event = '',
  conditions = {},
  onConditionsChange,
  conditionsModified = false,
  campaignId,
  initialFrequency = 'one_time',
}) => {
  const [frequency, setFrequency] = useState<TriggerFrequency>(
    initialFrequency === 'every_re_match' ? 'every_re_match' : 'one_time'
  );
  const frequencyModified = frequency !== initialFrequency;

  const handleSaveAttributeSegment = (data: any) => {
    onSave({
      trigger: 'attribute-segment',
      conditions: data.conditions,
      status: data.status,
      frequency,
    });
  };

  const handleSaveEvent = (data: {
    event: string;
    conditions: { [key: string]: Condition[] };
    status?: string;
  }) => {
    onSave({
      trigger: 'event',
      event: data.event,
      conditions: data.conditions,
      status: data.status,
      frequency,
    });
  };

  return (
    <div className="flex flex-col bg-card rounded-lg shadow-sm border border-border w-[30vw] h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Trigger</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              When someone enters this campaign
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-4 pt-4 shrink-0">
        <Select
          label="A profile can enter this automation:"
          options={[...TRIGGER_FREQUENCY_OPTIONS]}
          value={frequency}
          onChange={(e) => setFrequency(e.target.value as TriggerFrequency)}
          size="sm"
        />
      </div>

      {triggerType === 'event' ? (
        <TriggerEventPanel
          onClose={onClose}
          onSave={handleSaveEvent}
          initialEvent={event}
          initialConditions={conditions}
          onConditionsChange={onConditionsChange}
          conditionsModified={conditionsModified}
          frequencyModified={frequencyModified}
        />
      ) : (
        <TriggerConditionPanel
          onClose={onClose}
          onBack={() => {}}
          onSave={handleSaveAttributeSegment}
          initialConditions={conditions}
          onConditionsChange={onConditionsChange}
          conditionsModified={conditionsModified}
          frequencyModified={frequencyModified}
          hideBackButton
          campaignId={campaignId}
        />
      )}
    </div>
  );
};

export default TriggerPanel;
